import { useQuery } from '@tanstack/react-query'
import { getSubscriptions, type BillingState, type ShopQuota, type ItemQuota } from '@/requests/billing'
import { useShop } from '@/context/shop-context'
import { useAuthStore } from '@/stores/authStore'
import {
  getCachedBillingPlaceholder,
  setCachedPlan,
} from '@/lib/plan-cache'
import { PLAN_MAX_SHOPS, PLAN_MAX_ITEMS } from '@/lib/plan-limits'

function resolveShopId(shop: { _id?: string; id?: string } | null): string {
  if (!shop) return ''
  return shop._id ?? (shop as { id?: string }).id ?? ''
}

function syncUserPlan(plan: string): void {
  const { getUser, setUser } = useAuthStore.getState().auth
  const user = getUser()
  if (!user || user.role === plan) return
  setUser({ ...user, role: plan })
}

function fallbackShopQuota(plan: string, used = 0): ShopQuota {
  const max = PLAN_MAX_SHOPS[plan] ?? PLAN_MAX_SHOPS.FREE
  const max_shops = max ?? null
  return {
    used,
    max_shops,
    can_create: max_shops === null || used < max_shops,
  }
}

function fallbackItemQuota(plan: string, used = 0): ItemQuota {
  const max = PLAN_MAX_ITEMS[plan] ?? PLAN_MAX_ITEMS.FREE
  const max_items = max ?? null
  const remaining = max_items === null ? null : Math.max(0, max_items - used)
  return {
    used,
    max_items,
    remaining,
    can_create: max_items === null || used < max_items,
  }
}

export function useSubscriptions() {
  const { activeShop } = useShop()
  const shopId = resolveShopId(activeShop)

  return useQuery<BillingState>({
    queryKey: ['billing', 'subscriptions', shopId],
    queryFn: async () => {
      const data = await getSubscriptions(shopId)
      setCachedPlan(shopId, data.entitlements.plan)
      syncUserPlan(data.entitlements.plan)
      return data
    },
    enabled: Boolean(shopId),
    staleTime: 30_000,
    placeholderData: () => {
      const cached = getCachedBillingPlaceholder(shopId)
      if (!cached) return undefined
      const plan = cached.entitlements.plan
      return {
        ...cached,
        shop_quota: cached.shop_quota ?? fallbackShopQuota(plan),
        item_quota: cached.item_quota ?? fallbackItemQuota(plan),
      }
    },
  })
}
